import { matchPath } from "react-router-dom";

export type AppLayoutMode = "public" | "standard" | "wide" | "edge-to-edge";

export type RouteAccessPolicy = "unrestricted" | "non-acmi" | "acmi-only" | "manage-users" | "company-settings" | "super-admin";

export type NavigationPolicy = "ops" | "journey-logs" | "transfer" | "flights" | "improve-operation" | "files" | "library" | "fuel" | "reports" | "dashboard" | "fleets" | "admin";

export type AppRouteDefinition = {
  id: string;
  path: string;
  parentId?: string;
  layout: AppLayoutMode;
  accessPolicy: RouteAccessPolicy;
  navigationPolicy: NavigationPolicy | null;
  redirectTo?: string;
  deniedRedirectTo?: string;
};

export type RouteAccessContext = {
  isAcmiClient: boolean;
  isRefuelEuReader?: boolean;
  isCompanyAdmin: boolean;
  isSuperAdmin: boolean;
  canManageUsers: boolean;
};

export type NavigationVisibilityContext = {
  isAcmiClient: boolean;
  isRefuelEuReader?: boolean;
  hasFuelAccess: boolean;
  hasReportsAccess: boolean;
  hasTransferAccess: boolean;
  hasJourneyLogAccess?: boolean;
  isDashboardEnabled: boolean;
  showLibraryLink: boolean;
  showAdminLink: boolean;
};

export type AppNavigationDefinition = {
  id: NavigationPolicy;
  label: string;
  routeId: string;
};

export const canAccessRoute = (policy: RouteAccessPolicy, context: RouteAccessContext): boolean => {
  switch (policy) {
    case "unrestricted":
      return true;
    case "non-acmi":
      return !context.isAcmiClient;
    case "acmi-only":
      return context.isAcmiClient;
    case "manage-users":
      return !context.isAcmiClient && context.canManageUsers;
    case "company-settings":
      return !context.isAcmiClient && (context.isCompanyAdmin || context.isSuperAdmin);
    case "super-admin":
      return !context.isAcmiClient && context.isSuperAdmin;
    default:
      return false;
  }
};

export const appNavigationCatalog: AppNavigationDefinition[] = [
  { id: "ops", label: "Ops", routeId: "ops" },
  { id: "journey-logs", label: "JL", routeId: "journey-logs" },
  { id: "transfer", label: "Transfer", routeId: "ops-transfer-delay" },
  { id: "flights", label: "Flights", routeId: "flights" },
  {
    id: "improve-operation",
    label: "Improve your operation",
    routeId: "improve-operation",
  },
  { id: "files", label: "Files", routeId: "files" },
  { id: "library", label: "Library", routeId: "library" },
  { id: "fuel", label: "Fuel", routeId: "fuel" },
  { id: "reports", label: "Reports", routeId: "reports" },
  { id: "dashboard", label: "Dashboard", routeId: "dashboard" },
  { id: "fleets", label: "Fleets", routeId: "fleets" },
  { id: "admin", label: "Admin", routeId: "admin" },
];

export const isNavigationVisible = (policy: NavigationPolicy, context: NavigationVisibilityContext): boolean => {
  if (context.isRefuelEuReader) return policy === "fuel";
  if (context.isAcmiClient) {
    return policy === "flights" || policy === "improve-operation";
  }

  switch (policy) {
    case "improve-operation":
      return false;
    case "journey-logs":
      return context.hasJourneyLogAccess === true;
    case "transfer":
      return context.hasTransferAccess;
    case "library":
      return context.showLibraryLink;
    case "fuel":
      return context.hasFuelAccess;
    case "reports":
      return context.hasReportsAccess;
    case "dashboard":
    case "fleets":
      return context.isDashboardEnabled;
    case "admin":
      return context.showAdminLink;
    default:
      return true;
  }
};

const route = (definition: AppRouteDefinition): AppRouteDefinition => definition;

export const appRouteCatalog: AppRouteDefinition[] = [
  route({ id: "home", path: "/", layout: "public", accessPolicy: "unrestricted", navigationPolicy: null }),
  route({ id: "flights", path: "/flights", layout: "wide", accessPolicy: "unrestricted", navigationPolicy: "flights" }),
  route({ id: "flight-detail", path: "/flight/:id", parentId: "flights", layout: "wide", accessPolicy: "unrestricted", navigationPolicy: null }),
  route({ id: "improve-operation", path: "/improve-your-operation", layout: "standard", accessPolicy: "acmi-only", navigationPolicy: "improve-operation" }),
  route({ id: "files", path: "/files", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: "files" }),
  route({ id: "library", path: "/library", layout: "wide", accessPolicy: "manage-users", navigationPolicy: "library" }),
  route({ id: "reset-password", path: "/reset-password/:token", layout: "public", accessPolicy: "unrestricted", navigationPolicy: null }),
  route({ id: "reset-pin", path: "/reset-pin/:token", layout: "public", accessPolicy: "unrestricted", navigationPolicy: null }),
  route({ id: "users-legacy", path: "/users", layout: "standard", accessPolicy: "non-acmi", navigationPolicy: null, redirectTo: "/admin/users" }),
  route({ id: "dashboard", path: "/dashboard", layout: "edge-to-edge", accessPolicy: "non-acmi", navigationPolicy: "dashboard" }),
  route({ id: "dashboard-flight", path: "/dashboard/flight/:id", parentId: "dashboard", layout: "edge-to-edge", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "reports-refuel-legacy", path: "/reports/refuel-eu", parentId: "reports", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null, redirectTo: "/fuel/refuel-eu?tab=export" }),
  route({ id: "reports", path: "/reports", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: "reports" }),
  route({ id: "report-fuel", path: "/reports/fuel", parentId: "reports", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "report-approach-deicing", path: "/reports/approach-deicing", parentId: "reports", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "report-crew-duty", path: "/reports/crew-duty", parentId: "reports", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "report-otp", path: "/reports/otp", parentId: "reports", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "report-otp-day", path: "/reports/otp/day/:date", parentId: "reports", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "report-otp-turnaround", path: "/reports/otp-turnaround", parentId: "reports", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel", path: "/fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: "fuel" }),
  route({ id: "fuel-refuel-eu", path: "/fuel/refuel-eu", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-refuel-eu-unjustified", path: "/fuel/refuel-eu/unjustified", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-refuel-eu-year", path: "/fuel/refuel-eu/:year", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-refuel-eu-year-calculation", path: "/fuel/refuel-eu/:year/calculation", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-refuel-eu-year-airports", path: "/fuel/refuel-eu/:year/airports", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-refuel-eu-year-data-quality", path: "/fuel/refuel-eu/:year/data-quality", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-refuel-eu-year-delta-reasons", path: "/fuel/refuel-eu/:year/delta-reasons", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-refuel-eu-year-scope", path: "/fuel/refuel-eu/:year/scope/airports", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-refuel-eu-year-api-management", path: "/fuel/refuel-eu/:year/api-management", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-refuel-eu-year-reports", path: "/fuel/refuel-eu/:year/reports", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-assurance", path: "/fuel/assurance", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-assurance-flights", path: "/fuel/assurance/flights", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-assurance-flight", path: "/fuel/assurance/flights/:flightId", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-assurance-entities", path: "/fuel/assurance/entities", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-assurance-trends", path: "/fuel/assurance/trends", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-assurance-opportunities", path: "/fuel/assurance/opportunities", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-assurance-scf", path: "/fuel/assurance/scf", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "fuel-assurance-data-quality", path: "/fuel/assurance/data-quality", parentId: "fuel", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "refuel-eu", path: "/refuel-eu", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null, redirectTo: "/fuel/refuel-eu" }),
  route({ id: "refuel-eu-unjustified", path: "/refuel-eu/unjustified", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null, redirectTo: "/fuel/refuel-eu/unjustified" }),
  route({ id: "fleets", path: "/fleets", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: "fleets" }),
  route({ id: "ops", path: "/ops", layout: "edge-to-edge", accessPolicy: "non-acmi", navigationPolicy: "ops" }),
  route({ id: "journey-logs", path: "/ops/journey-logs", parentId: "ops", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: "journey-logs" }),
  route({ id: "journey-log", path: "/ops/journey-logs/:id", parentId: "ops", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "ops-transfer-delay", path: "/ops/transfer-delay", parentId: "ops", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: "transfer" }),
  route({ id: "ops-distribution", path: "/ops/flights/:flightId/distribution", parentId: "ops", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "profile", path: "/profile", layout: "standard", accessPolicy: "non-acmi", navigationPolicy: null }),
  route({ id: "operators-legacy", path: "/operators", layout: "standard", accessPolicy: "non-acmi", navigationPolicy: null, redirectTo: "/admin/operators" }),
  route({ id: "admin", path: "/admin", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: "admin" }),
  route({ id: "admin-users", path: "/admin/users", parentId: "admin", layout: "wide", accessPolicy: "manage-users", navigationPolicy: null }),
  route({ id: "admin-customer-data-share", path: "/admin/customer-data-share", parentId: "admin", layout: "wide", accessPolicy: "manage-users", navigationPolicy: null }),
  route({ id: "admin-operators", path: "/admin/operators", parentId: "admin", layout: "wide", accessPolicy: "super-admin", navigationPolicy: null, deniedRedirectTo: "/admin/operator-settings" }),
  route({ id: "admin-operator-settings", path: "/admin/operator-settings", parentId: "admin", layout: "wide", accessPolicy: "company-settings", navigationPolicy: null }),
  route({ id: "admin-operator-services", path: "/admin/operator-services", parentId: "admin", layout: "wide", accessPolicy: "company-settings", navigationPolicy: null }),
  route({ id: "admin-accounting", path: "/admin/accounting", parentId: "admin", layout: "wide", accessPolicy: "super-admin", navigationPolicy: null }),
  route({ id: "admin-leon-transfer", path: "/admin/leon-transfer", parentId: "admin", layout: "wide", accessPolicy: "company-settings", navigationPolicy: null }),
  route({ id: "admin-leon-id", path: "/admin/leon-id", parentId: "admin", layout: "wide", accessPolicy: "company-settings", navigationPolicy: null }),
  route({ id: "admin-email-distribution", path: "/admin/email-distribution", parentId: "admin", layout: "wide", accessPolicy: "company-settings", navigationPolicy: null }),
  route({ id: "admin-email-footer", path: "/admin/email-footer", parentId: "admin", layout: "wide", accessPolicy: "company-settings", navigationPolicy: null }),
  route({ id: "admin-smart-ofp-legacy", path: "/admin/smart-ofp-settings", parentId: "admin", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null, redirectTo: "/admin/operator-settings" }),
  route({ id: "admin-pdf", path: "/admin/pdf", parentId: "admin", layout: "wide", accessPolicy: "company-settings", navigationPolicy: null }),
  route({ id: "admin-status", path: "/admin/status", parentId: "admin", layout: "wide", accessPolicy: "super-admin", navigationPolicy: null }),
  route({ id: "admin-settings", path: "/admin/settings", parentId: "admin", layout: "wide", accessPolicy: "company-settings", navigationPolicy: null }),
  route({ id: "admin-logs", path: "/admin/logs", parentId: "admin", layout: "wide", accessPolicy: "company-settings", navigationPolicy: null }),
  route({ id: "admin-mail-settings", path: "/admin/mail-settings", parentId: "admin", layout: "wide", accessPolicy: "super-admin", navigationPolicy: null }),
  route({ id: "admin-maintenance", path: "/admin/maintenance", parentId: "admin", layout: "wide", accessPolicy: "super-admin", navigationPolicy: null }),
  route({ id: "admin-nextclouds", path: "/admin/nextclouds", parentId: "admin", layout: "wide", accessPolicy: "super-admin", navigationPolicy: null }),
  route({ id: "admin-fallback", path: "/admin/*", parentId: "admin", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null, redirectTo: "/admin" }),
  route({ id: "library-user-report", path: "/users/:id/library/report", parentId: "library", layout: "wide", accessPolicy: "non-acmi", navigationPolicy: null, redirectTo: "/library?tab=compliance&userId=:id" }),
  route({ id: "not-found", path: "*", layout: "standard", accessPolicy: "unrestricted", navigationPolicy: null }),
];

export const getRouteDefinition = (id: string): AppRouteDefinition | null => appRouteCatalog.find((definition) => definition.id === id) || null;

const routeSpecificity = (path: string): number => {
  if (path === "*") {
    return -1;
  }
  return path
    .split("/")
    .filter(Boolean)
    .reduce((score, segment) => score + (segment.startsWith(":") ? 2 : segment === "*" ? 0 : 4), 0);
};

export const getRouteDefinitionForPathname = (pathname: string): AppRouteDefinition => {
  const match = appRouteCatalog
    .filter((definition) =>
      definition.path === "*"
        ? true
        : Boolean(matchPath({ path: definition.path, end: true }, pathname)),
    )
    .sort((left, right) => routeSpecificity(right.path) - routeSpecificity(left.path))[0];

  return match || requireRouteDefinition("not-found");
};

export const requireRouteDefinition = (id: string): AppRouteDefinition => {
  const definition = getRouteDefinition(id);
  if (!definition) {
    throw new Error(`Unknown application route: ${id}`);
  }
  return definition;
};

export const getRoutePath = (id: string): string => requireRouteDefinition(id).path;

export const getRelativeRoutePath = (id: string): string => {
  const definition = requireRouteDefinition(id);
  if (!definition.parentId) {
    return definition.path;
  }

  const parentPath = requireRouteDefinition(definition.parentId).path;
  const prefix = `${parentPath}/`;
  if (!definition.path.startsWith(prefix)) {
    throw new Error(`Route ${id} is not nested below ${definition.parentId}`);
  }
  return definition.path.slice(prefix.length);
};

export const getDeniedRedirect = (id: string): string | null => {
  const definition = requireRouteDefinition(id);
  if (definition.deniedRedirectTo) {
    return definition.deniedRedirectTo;
  }

  switch (definition.accessPolicy) {
    case "unrestricted":
      return null;
    case "non-acmi":
      return "/flights";
    case "acmi-only":
    case "manage-users":
    case "company-settings":
    case "super-admin":
      return "/ops";
    default:
      return null;
  }
};

export type RouteAccessResolution = {
  allowed: boolean;
  deniedRedirectTo: string | null;
};

export const resolveRouteAccess = (id: string, context: RouteAccessContext): RouteAccessResolution => {
  const definition = requireRouteDefinition(id);
  if (context.isRefuelEuReader) {
    const allowed = ["home", "profile", "fuel", "reset-password", "reports-refuel-legacy", "refuel-eu", "refuel-eu-unjustified"].includes(id) ||
      id.startsWith("fuel-refuel-eu");
    return { allowed, deniedRedirectTo: allowed ? null : "/fuel/refuel-eu" };
  }
  const allowed = canAccessRoute(definition.accessPolicy, context);
  return {
    allowed,
    deniedRedirectTo: allowed ? null : getDeniedRedirect(id),
  };
};

export const getRouteRedirect = (id: string, params: Record<string, string> = {}): string => {
  const redirectTo = requireRouteDefinition(id).redirectTo;
  if (!redirectTo) {
    throw new Error(`Route ${id} does not define a redirect`);
  }

  return redirectTo.replace(/:([A-Za-z0-9_]+)/g, (_match, key: string) => params[key] || "");
};

export const buildNavigationItems = (context: NavigationVisibilityContext): Array<AppNavigationDefinition & { to: string; isVisible: boolean }> =>
  appNavigationCatalog.map((definition) => {
    const routeDefinition = requireRouteDefinition(definition.routeId);
    if (routeDefinition.navigationPolicy !== definition.id) {
      throw new Error(`Navigation ${definition.id} is not linked to route ${definition.routeId}`);
    }
    return {
      ...definition,
      label: context.isRefuelEuReader && definition.id === "fuel" ? "RefuelEU" : definition.label,
      to: context.isRefuelEuReader && definition.id === "fuel" ? "/fuel/refuel-eu" : routeDefinition.path,
      isVisible: isNavigationVisible(definition.id, context),
    };
  });
